// Custom hook for managing Sadhana data with Firebase integration
import { useState, useEffect } from 'react';
import { sadhanaService } from '../firebase/sadhanaService';

export const useSadhanaData = () => {
  const [sadhanaData, setSadhanaData] = useState({
    date: null,
    location: null,
    id: null,
    timestamp: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load sadhana data from Firebase
  const loadSadhanaData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await sadhanaService.getCurrentSadhanaInfo();
      
      if (result.success) {
        if (result.data && !result.data.cleared) {
          setSadhanaData({
            date: result.data.date,
            location: result.data.location,
            id: result.data.id,
            timestamp: result.data.timestamp
          });
        } else {
          setSadhanaData({ date: null, location: null, id: null, timestamp: null });
        }
      } else {
        setError(result.error);
        // Fallback to localStorage if Firebase fails
        const fallbackData = loadFromLocalStorage();
        setSadhanaData(fallbackData);
      }
    } catch (err) {
      console.error('Error loading sadhana data:', err);
      setError(err.message);
      // Fallback to localStorage
      const fallbackData = loadFromLocalStorage();
      setSadhanaData(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  // Save sadhana data to Firebase
  const saveSadhanaData = async (date, location) => {
    try {
      setLoading(true);
      setError(null);

      const dataToSave = {
        date: date ? (date instanceof Date ? date.toISOString() : date) : null,
        location: location || null
      };

      const result = await sadhanaService.saveSadhanaInfo(dataToSave);
      
      if (result.success) {
        setSadhanaData(dataToSave);
        // Also save to localStorage as backup
        saveToLocalStorage(dataToSave);
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Error saving sadhana data:', err);
      setError(err.message);
      // Fallback to localStorage
      const dataToSave = {
        date: date ? (date instanceof Date ? date.toISOString() : date) : null,
        location: location || null
      };
      saveToLocalStorage(dataToSave);
      setSadhanaData(dataToSave);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Clear sadhana data
  const clearSadhanaData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await sadhanaService.clearSadhanaInfo();
      
      if (result.success) {
        setSadhanaData({ date: null, location: null });
        // Also clear localStorage
        localStorage.removeItem('sadhanaDate');
        localStorage.removeItem('sadhanaLocation');
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Error clearing sadhana data:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // LocalStorage fallback functions
  const loadFromLocalStorage = () => {
    const savedDate = localStorage.getItem('sadhanaDate');
    const savedLocation = localStorage.getItem('sadhanaLocation');
    return {
      date: savedDate || null,
      location: savedLocation || null
    };
  };

  const saveToLocalStorage = (data) => {
    if (data.date) {
      localStorage.setItem('sadhanaDate', data.date);
    } else {
      localStorage.removeItem('sadhanaDate');
    }
    
    if (data.location) {
      localStorage.setItem('sadhanaLocation', data.location);
    } else {
      localStorage.removeItem('sadhanaLocation');
    }
  };

  // Format date for display
  const getFormattedDate = () => {
    if (!sadhanaData.date) return '';
    
    try {
      const date = new Date(sadhanaData.date);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
    } catch (error) {
      console.error('Error formatting date:', error);
    }
    return '';
  };

  // Get location label
  const getLocationLabel = () => {
    const locationLabels = {
      'high-park': 'High Park, Toronto',
      'trinity-bellwoods': 'Trinity Bellwoods Park, Toronto',
      'centre-island': 'Centre Island, Toronto',
      'harbourfront': 'Harbourfront Centre, Toronto',
      'online': 'Online Session'
    };
    
    return locationLabels[sadhanaData.location] || sadhanaData.location || '';
  };

  // Load data on mount
  useEffect(() => {
    loadSadhanaData();
  }, []);

  return {
    sadhanaData,
    loading,
    error,
    saveSadhanaData,
    clearSadhanaData,
    refreshData: loadSadhanaData,
    formattedDate: getFormattedDate(),
    locationLabel: getLocationLabel()
  };
};
